import React, { useState } from 'react';
import { X, Mail, User, MessageSquare, Send, CheckCircle, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { useToast } from '../../hooks/use-toast';
import waitlistService from '../../services/waitlistService';
import { useAuth } from '../../contexts/AuthContext';

interface WaitlistModalProps {
  isOpen: boolean;
  onClose: () => void;
  tierName: string;
  featureName: string;
}

const WaitlistModal: React.FC<WaitlistModalProps> = ({
  isOpen,
  onClose,
  tierName,
  featureName
}) => {
  const { toast } = useToast();
  const { user } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [formData, setFormData] = useState({
    email: user?.email || '',
    user_name: user?.first_name && user?.last_name 
      ? `${user.first_name} ${user.last_name}` 
      : user?.first_name || '',
    additional_info: ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.email) {
      toast({
        title: 'Email Required',
        description: 'Please enter your email address',
        variant: 'destructive'
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await waitlistService.joinWaitlist({
        email: formData.email,
        feature_name: featureName,
        user_name: formData.user_name,
        additional_info: formData.additional_info
      });

      if (response.success) {
        setIsSuccess(true);
        toast({
          title: 'Successfully Joined!',
          description: response.message,
        });
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to join waitlist. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setIsSuccess(false);
    setFormData({
      email: user?.email || '',
      user_name: user?.first_name && user?.last_name 
        ? `${user.first_name} ${user.last_name}` 
        : user?.first_name || '',
      additional_info: ''
    });
    onClose();
  };

  return (
    <div>
      <style>{`
        [data-state="open"][data-radix-dialog-overlay] {
          background: rgba(0, 0, 0, 0.7) !important;
          backdrop-filter: blur(8px) !important;
        }
        .fixed.inset-0.z-50.bg-background\\/80 {
          background: rgba(0, 0, 0, 0.7) !important;
          backdrop-filter: blur(8px) !important;
        }
        [data-radix-dialog-overlay],
        [data-overlay] {
          background: rgba(0, 0, 0, 0.7) !important;
          backdrop-filter: blur(8px) !important;
        }
      `}</style>
      <Dialog open={isOpen} onOpenChange={handleClose}>
        {isOpen && (
          <div 
            className="fixed inset-0 bg-black/70 backdrop-blur-sm"
            style={{ zIndex: 49 }}
          />
        )}
        <DialogContent 
          className="sm:max-w-md bg-gray-800/95 backdrop-blur-xl border border-gray-600/40 shadow-2xl"
          style={{ zIndex: 51 }}
        >
        <DialogHeader className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #30b3dd 0%, #4a298a 50%, #30b3dd 100%)', boxShadow: '0 4px 15px rgba(48, 179, 221, 0.3)' }}>
                <Mail className="w-5 h-5 text-white" />
              </div>
              <div>
                <DialogTitle className="text-xl font-bold text-white">
                  Join {tierName} Waitlist
                </DialogTitle>
                <p className="text-sm text-gray-300">
                  Be the first to know when {tierName} launches
                </p>
              </div>
            </div>
          </div>
        </DialogHeader>

        {isSuccess ? (
          <div className="py-8 text-center">
            <div className="w-16 h-16 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              You're on the list!
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              We'll notify you as soon as {tierName} is available with special launch pricing.
            </p>
            <Button onClick={handleClose} className="w-full">
              Got it!
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-semibold text-white">
                  Email Address *
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="your@email.com"
                    className="pl-10 bg-gray-700/50 border border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 text-white placeholder-gray-400"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="user_name" className="text-sm font-semibold text-white">
                  Name (Optional)
                </Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="user_name"
                    type="text"
                    value={formData.user_name}
                    onChange={(e) => setFormData({ ...formData, user_name: e.target.value })}
                    placeholder="Your name"
                    className="pl-10 bg-gray-700/50 border border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 text-white placeholder-gray-400"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="additional_info" className="text-sm font-semibold text-white">
                  What interests you most? (Optional)
                </Label>
                <div className="relative">
                  <MessageSquare className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                  <Textarea
                    id="additional_info"
                    value={formData.additional_info}
                    onChange={(e) => setFormData({ ...formData, additional_info: e.target.value })}
                    placeholder="Tell us what features you're most excited about..."
                    rows={3}
                    className="pl-10 pt-3 bg-gray-700/50 border border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 text-white placeholder-gray-400 resize-none"
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-3 pt-4 border-t border-gray-600/40">
              <Button 
                type="button" 
                variant="outline" 
                onClick={handleClose}
                className="flex-1 border-gray-600 hover:bg-gray-700 text-white"
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={isSubmitting || !formData.email}
                className="flex-1 text-white shadow-lg transition-all duration-300"
                style={{ 
                  background: 'linear-gradient(135deg, #30b3dd 0%, #4a298a 50%, #30b3dd 100%)',
                  backgroundSize: '200% 200%',
                  backgroundPosition: 'left center',
                  boxShadow: '0 4px 15px rgba(48, 179, 221, 0.3)'
                }}
                onMouseEnter={(e) => {
                  if (!isSubmitting && formData.email) {
                    e.currentTarget.style.backgroundPosition = 'right center';
                    e.currentTarget.style.boxShadow = '0 6px 20px rgba(48, 179, 221, 0.4)';
                    e.currentTarget.style.transform = 'translateY(-1px) scale(1.05)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundPosition = 'left center';
                  e.currentTarget.style.boxShadow = '0 4px 15px rgba(48, 179, 221, 0.3)';
                  e.currentTarget.style.transform = 'translateY(0) scale(1)';
                }}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Joining...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Join Waitlist
                  </>
                )}
              </Button>
            </div>
          </form>
        )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export { WaitlistModal };
export default WaitlistModal;